import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { generateAIText, getAIEmailPrompts } from '../../../../api'
import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../../constants/common'
import { useApi } from '../../../../hooks'
import { isValueStringType } from '../../../../utils/common'
import { htmlToPlainText, plainTextToHtml } from '../../../../utils/html'
import {
  AI_LENGTH_OPTIONS,
  AI_PROMPT_MAX_LENGTH,
  AI_TEXT_GENERATION_FORM_FIELD_NAME_LENGTH,
  AI_TEXT_GENERATION_FORM_FIELD_NAME_PROMPT,
  AI_TEXT_GENERATION_FORM_FIELD_NAME_REMOVE_NOTICE,
  AI_TEXT_GENERATION_FORM_FIELD_NAME_TONE,
  AI_TEXT_GENERATION_FORM_INITIAL_VALUES,
  AI_TEXT_GENERATION_SERVER_ERROR_PROMPT_TOO_LONG,
  AI_TEXT_GENERATION_SERVER_ERRORS,
  AI_TONE_OPTIONS,
  AITextGenerationType,
} from '../../AITextGeneration.constants'
import {
  isAITextGenerationTypeEmail,
  normalizeAITextGenerationTemplateOptions,
  parseAITextGenerationServerError,
} from '../../AITextGeneration.utils'

export function useAITextGenerationState({
  fieldContent,
  localContext,
  type = AITextGenerationType.Email,
  onGenerate,
  onGenerateStart,
  onGenerateStop,
} = EMPTY_OBJECT) {
  const { t } = useTranslation()

  const templatesApi = useApi()
  const generateApi = useApi()

  const fieldContentRef = useRef(fieldContent)
  const onGenerateRef = useRef(onGenerate)
  const onGenerateStartRef = useRef(onGenerateStart)
  const onGenerateStopRef = useRef(onGenerateStop)
  const lastPromptTextRef = useRef(EMPTY_STRING)

  useEffect(() => {
    fieldContentRef.current = fieldContent
    onGenerateRef.current = onGenerate
    onGenerateStartRef.current = onGenerateStart
    onGenerateStopRef.current = onGenerateStop
  }, [fieldContent, onGenerate, onGenerateStart, onGenerateStop])

  const [templates, setTemplates] = useState(EMPTY_LIST)
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(true)

  const [isGenerating, setIsGenerating] = useState(false)
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false)
  const [lastSubmittedSettings, setLastSubmittedSettings] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)

  useEffect(() => {
    templatesApi
      .call(getAIEmailPrompts)
      .then(response => {
        if (response) {
          setTemplates(normalizeAITextGenerationTemplateOptions(response))
        }
      })
      .finally(() => {
        setIsTemplatesLoading(false)
      })
  }, [templatesApi])

  const toneOptions = useMemo(
    () =>
      AI_TONE_OPTIONS.map(({ labelKey, value }) => ({
        label: t(labelKey),
        value,
      })),
    [t]
  )

  const lengthOptions = useMemo(
    () =>
      AI_LENGTH_OPTIONS.map(({ Icon, labelKey, value }) => ({
        Icon,
        label: t(labelKey),
        value,
      })),
    [t]
  )

  const initialValues = useMemo(
    () => ({
      ...AI_TEXT_GENERATION_FORM_INITIAL_VALUES,
      [AI_TEXT_GENERATION_FORM_FIELD_NAME_LENGTH]: lengthOptions[1],
      [AI_TEXT_GENERATION_FORM_FIELD_NAME_TONE]: toneOptions[0],
    }),
    [lengthOptions, toneOptions]
  )

  const handleSubmit = useCallback(
    (values, { setSubmitting }) => {
      setErrorMessage(null)

      const promptText =
        values?.[AI_TEXT_GENERATION_FORM_FIELD_NAME_PROMPT] ||
        htmlToPlainText(fieldContentRef.current) ||
        lastPromptTextRef.current ||
        EMPTY_STRING

      lastPromptTextRef.current = promptText

      if (promptText.length > AI_PROMPT_MAX_LENGTH) {
        setErrorMessage(
          t(
            AI_TEXT_GENERATION_SERVER_ERRORS[
              AI_TEXT_GENERATION_SERVER_ERROR_PROMPT_TOO_LONG
            ]
          )
        )
        setSubmitting(false)
        onGenerateStopRef.current?.()

        return
      }

      const toneValue =
        values?.[AI_TEXT_GENERATION_FORM_FIELD_NAME_TONE]?.value || null
      const lengthValue =
        values?.[AI_TEXT_GENERATION_FORM_FIELD_NAME_LENGTH]?.value || null
      const removeNotice = Boolean(
        values?.[AI_TEXT_GENERATION_FORM_FIELD_NAME_REMOVE_NOTICE]
      )

      setLastSubmittedSettings({
        length: lengthValue,
        prompt: promptText,
        tone: toneValue,
      })

      const payload = {
        length: lengthValue,
        local_context:
          localContext && isAITextGenerationTypeEmail(type)
            ? localContext
            : null,
        prompt_text: promptText,
        tone: toneValue,
        type,
      }

      setIsGenerating(true)

      onGenerateStartRef.current?.()

      generateApi
        .call(generateAIText, { data: payload })
        .then(response => {
          if (response) {
            const responseData = response?.data || null

            const rawText =
              responseData?.body ??
              responseData?.text ??
              (isValueStringType(responseData) ? responseData : EMPTY_STRING)

            const generatedText = plainTextToHtml(rawText)
            const generatedSubject = responseData?.subject || EMPTY_STRING

            onGenerateRef.current?.(
              generatedText,
              promptText,
              !removeNotice,
              generatedSubject
            )
          }
        })
        .catch(error => {
          const parsedError = parseAITextGenerationServerError(error, t)

          setErrorMessage(parsedError || t('Error.CommonApiRequestFailKey'))
          onGenerateStopRef.current?.()
        })
        .finally(() => {
          setHasGeneratedOnce(true)
          setIsGenerating(false)
          setSubmitting(false)
        })
    },
    [generateApi, localContext, t, type]
  )

  return {
    errorMessage,
    hasGeneratedOnce,
    initialValues,
    isGenerating,
    isTemplatesLoading,
    lastSubmittedSettings,
    lengthOptions,
    templates,
    toneOptions,
    onSubmit: handleSubmit,
  }
}
